/**
 * EditorToolbar — Left-side toolbar for MapEditorScene.
 *
 * Builds the mode buttons, tile/decor/resource palettes, grid size controls,
 * undo/redo buttons, and back button.
 */
import Phaser from 'phaser';
import { TileType, DecorType, CANVAS_HEIGHT } from '@/config/GameConfig';
import { SoundManager } from '@/systems/SoundManager';
import { TECH, drawTechRect, createSmallTechButton } from '@/ui/TechUI';
import {
  EditorMode,
  TOOLBAR_W, TOOLBAR_X, GRID_AREA_X,
  STRUCTURE_TILE_TOOLS, SPECIAL_TILE_TOOLS, ACTIVE_TILE_TOOLS,
  DECOR_TOOLS, RESOURCE_TOOLS, TILE_TEXTURES,
} from './EditorTypes';

/** Minimal interface for the scene methods the toolbar needs. */
export interface ToolbarHost {
  scene: Phaser.Scene;
  selectedTile: TileType;
  selectedDecor: DecorType;
  mode: EditorMode;
  campaignLevelId: string | null;
  returnTo: string;
  gridCols: number;
  gridRows: number;

  selectTile(type: TileType): void;
  setMode(mode: EditorMode): void;
  resizeGrid(cols: number, rows: number): void;
  undo(): void;
  redo(): void;
  renderGrid(): void;
  updateSizeText(): void;
}

export interface ToolbarRefs {
  toolBtns: Map<TileType, Phaser.GameObjects.Rectangle>;
  modeBtns: Map<EditorMode, Phaser.GameObjects.Container>;
  decorBtns: Map<DecorType, Phaser.GameObjects.Rectangle>;
  resourceBtns: Map<DecorType, Phaser.GameObjects.Rectangle>;
  statusText: Phaser.GameObjects.Text;
  sizeText: Phaser.GameObjects.Text;
  undoText: Phaser.GameObjects.Text;
  pathListText: Phaser.GameObjects.Text;
  paintSection: Phaser.GameObjects.Container;
  pathSection: Phaser.GameObjects.Container;
  decorSection: Phaser.GameObjects.Container;
  resourceSection: Phaser.GameObjects.Container;
  eraseSection: Phaser.GameObjects.Container;
  paletteSectionY: number;
  paletteVisibleH: number;
}

/**
 * Build the full left-side toolbar. Returns refs to all created UI elements
 * so the scene can wire them into its own fields.
 */
export function buildToolbar(host: ToolbarHost): ToolbarRefs {
  const scene = host.scene;
  const toolBtns = new Map<TileType, Phaser.GameObjects.Rectangle>();
  const modeBtns = new Map<EditorMode, Phaser.GameObjects.Container>();
  const decorBtns = new Map<DecorType, Phaser.GameObjects.Rectangle>();
  const resourceBtns = new Map<DecorType, Phaser.GameObjects.Rectangle>();

  // Panel background
  scene.add.rectangle(TOOLBAR_X + TOOLBAR_W / 2, CANVAS_HEIGHT / 2, TOOLBAR_W, CANVAS_HEIGHT, 0x111122, 0.95)
    .setStrokeStyle(1, 0x333355).setDepth(10);

  // Title
  scene.add.text(10, 8, 'MAP EDITOR', {
    fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
  }).setDepth(10);

  // ── Mode buttons (always visible) ──
  let y = 30;
  scene.add.text(10, y, 'Mode:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' }).setDepth(10);
  y += 14;

  for (const mode of [EditorMode.PAINT, EditorMode.DECOR, EditorMode.RESOURCE, EditorMode.ERASE, EditorMode.PATH]) {
    const label = mode === EditorMode.PAINT ? 'Paint Tiles' :
                  mode === EditorMode.PATH ? 'Edit Paths' :
                  mode === EditorMode.RESOURCE ? 'Resources' :
                  mode === EditorMode.ERASE ? 'Eraser' : 'Decorations';
    const btn = createSmallBtn(scene, 10, y, TOOLBAR_W - 20, 22, label, () => {
      host.setMode(mode);
    });
    btn.setDepth(10);
    modeBtns.set(mode, btn);
    y += 26;
  }

  // ══ MODE-DEPENDENT SECTIONS ══
  const sectionY = y + 4;

  // ── PAINT section ──
  const paintSection = scene.add.container(0, 0).setDepth(10);
  let py = sectionY;
  addTextToContainer(scene, paintSection, 10, py, 'Structure:', '10px', '#aaaaaa');
  py += 14;

  const addTileBtn = (tool: typeof STRUCTURE_TILE_TOOLS[0]) => {
    const texKey = TILE_TEXTURES[tool.type];
    const img = scene.add.image(25, py + 20, texKey)
      .setDisplaySize(40, 40)
      .setInteractive({ useHandCursor: true });
    img.on('pointerdown', () => { host.selectTile(tool.type); });
    paintSection.add(img);

    const border = scene.add.rectangle(25, py + 20, 44, 44)
      .setStrokeStyle(2, tool.type === host.selectedTile ? 0xffcc00 : 0x333355)
      .setFillStyle(0x000000, 0);
    border.on('pointerdown', () => { host.selectTile(tool.type); });
    toolBtns.set(tool.type, border);
    paintSection.add(border);

    const label = scene.add.text(50, py + 14, `${tool.key} ${tool.label}`, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    label.on('pointerdown', () => { host.selectTile(tool.type); });
    paintSection.add(label);
    py += 46;
  };

  for (const tool of STRUCTURE_TILE_TOOLS) addTileBtn(tool);

  // Separator + Special Tiles header
  py += 4;
  const sep1 = scene.add.graphics();
  sep1.lineStyle(1, 0x444466, 0.5);
  sep1.lineBetween(10, py, TOOLBAR_W - 10, py);
  paintSection.add(sep1);
  py += 6;
  addTextToContainer(scene, paintSection, 10, py, 'Special Tiles:', '10px', '#44aacc');
  py += 14;

  for (const tool of SPECIAL_TILE_TOOLS) addTileBtn(tool);

  // Separator + Active tiles header
  py += 4;
  const sep2 = scene.add.graphics();
  sep2.lineStyle(1, 0x444466, 0.5);
  sep2.lineBetween(10, py, TOOLBAR_W - 10, py);
  paintSection.add(sep2);
  py += 6;
  addTextToContainer(scene, paintSection, 10, py, 'Active Tiles:', '10px', '#cc8844');
  py += 14;

  for (const tool of ACTIVE_TILE_TOOLS) addTileBtn(tool);

  // ── PATH section ──
  const pathSection = scene.add.container(0, 0).setVisible(false).setDepth(10);
  py = sectionY;
  addTextToContainer(scene, pathSection, 10, py, 'Path Tools:', '10px', '#aaaaaa');
  py += 14;

  // Path buttons will be wired by the scene to PathEditor methods via events
  pathSection.add(createSmallBtn(scene, 10, py, TOOLBAR_W - 20, 22, 'Finish Path (ESC)', () => {
    scene.events.emit('toolbar:finishPath');
  }));
  py += 26;
  pathSection.add(createSmallBtn(scene, 10, py, TOOLBAR_W - 20, 22, 'Delete Last Path', () => {
    scene.events.emit('toolbar:deleteLastPath');
  }));
  py += 26;
  pathSection.add(createSmallBtn(scene, 10, py, TOOLBAR_W - 20, 22, 'Auto Paths', () => {
    scene.events.emit('toolbar:autoGeneratePaths');
  }));
  py += 30;

  addTextToContainer(scene, pathSection, 10, py, 'Paths:', '10px', '#aaaaaa');
  py += 14;
  const pathListText = scene.add.text(10, py, '(none)', {
    fontSize: '9px', color: '#888888', fontFamily: 'monospace',
    wordWrap: { width: TOOLBAR_W - 20 },
  });
  pathListText.setCrop(0, 0, TOOLBAR_W - 20, 200);
  pathSection.add(pathListText);

  // ── DECOR section ──
  const decorSection = scene.add.container(0, 0).setVisible(false).setDepth(10);
  py = sectionY;
  addTextToContainer(scene, decorSection, 10, py, 'Decor:', '10px', '#aaaaaa');
  py += 14;

  for (const dt of DECOR_TOOLS) {
    const border = scene.add.rectangle(25, py + 20, 44, 44, 0x000000, 0)
      .setStrokeStyle(2, dt.type === host.selectedDecor ? 0xffcc00 : 0x333355)
      .setInteractive({ useHandCursor: true });
    const img = scene.add.image(25, py + 20, dt.texture).setDisplaySize(40, 40);
    border.on('pointerdown', () => {
      host.selectedDecor = dt.type;
      for (const [t, rect] of decorBtns) {
        rect.setStrokeStyle(2, t === dt.type ? 0xffcc00 : 0x333355);
      }
    });
    decorBtns.set(dt.type, border);
    decorSection.add(border);
    decorSection.add(img);

    const lbl = scene.add.text(50, py + 14, dt.label, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        host.selectedDecor = dt.type;
        for (const [t, rect] of decorBtns) {
          rect.setStrokeStyle(2, t === dt.type ? 0xffcc00 : 0x333355);
        }
      });
    decorSection.add(lbl);
    py += 46;
  }

  addTextToContainer(scene, decorSection, 10, py + 6, 'Drag to paint decor.', '9px', '#666688');

  // ── RESOURCE section ──
  const resourceSection = scene.add.container(0, 0).setVisible(false).setDepth(10);
  py = sectionY;
  addTextToContainer(scene, resourceSection, 10, py, 'Resources:', '10px', '#aaaaaa');
  py += 14;

  for (const rt of RESOURCE_TOOLS) {
    const border = scene.add.rectangle(25, py + 20, 44, 44, 0x000000, 0)
      .setStrokeStyle(2, rt.type === host.selectedDecor ? 0xffcc00 : 0x333355)
      .setInteractive({ useHandCursor: true });
    const img = scene.add.image(25, py + 20, rt.texture).setDisplaySize(40, 40);
    border.on('pointerdown', () => {
      host.selectedDecor = rt.type;
      for (const [t, rect] of decorBtns) {
        rect.setStrokeStyle(2, t === rt.type ? 0xffcc00 : 0x333355);
      }
      for (const [t, rect] of resourceBtns) {
        rect.setStrokeStyle(2, t === rt.type ? 0xffcc00 : 0x333355);
      }
    });
    resourceBtns.set(rt.type, border);
    resourceSection.add(border);
    resourceSection.add(img);

    const lbl = scene.add.text(50, py + 14, rt.label, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        host.selectedDecor = rt.type;
        for (const [t, rect] of decorBtns) {
          rect.setStrokeStyle(2, t === rt.type ? 0xffcc00 : 0x333355);
        }
        for (const [t, rect] of resourceBtns) {
          rect.setStrokeStyle(2, t === rt.type ? 0xffcc00 : 0x333355);
        }
      });
    resourceSection.add(lbl);

    const yieldLbl = scene.add.text(50, py + 28, rt.yield, {
      fontSize: '8px', color: '#66aa66', fontFamily: 'monospace',
    });
    resourceSection.add(yieldLbl);
    py += 46;
  }

  addTextToContainer(scene, resourceSection, 10, py + 6, 'Drag to place.\nYields resources\nwhen destroyed.', '9px', '#666688');

  // ── ERASE section ──
  const eraseSection = scene.add.container(0, 0).setVisible(false).setDepth(10);
  py = sectionY;
  addTextToContainer(scene, eraseSection, 10, py, 'Eraser:', '10px', '#aaaaaa');
  py += 14;
  addTextToContainer(scene, eraseSection, 10, py, 'Click/drag to erase\ntiles and decorations.\nResets tile to Ground.', '9px', '#666688');

  // ── Palette scrolling setup ──
  const paletteSectionY = sectionY;
  const paletteVisibleH = CANVAS_HEIGHT - 130 - sectionY - 6;
  const maskShape = scene.make.graphics({ x: 0, y: 0 });
  maskShape.fillStyle(0xffffff);
  maskShape.fillRect(0, sectionY, TOOLBAR_W, paletteVisibleH);
  const paletteMask = maskShape.createGeometryMask();
  paintSection.setMask(paletteMask);
  decorSection.setMask(paletteMask);
  resourceSection.setMask(paletteMask);

  // ══ BOTTOM SECTION (always visible) ══
  y = CANVAS_HEIGHT - 130;

  // Grid size
  scene.add.text(10, y, 'Grid:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' }).setDepth(10);
  const sizeText = scene.add.text(50, y, `${host.gridCols} × ${host.gridRows}`, {
    fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
  }).setDepth(10);
  y += 18;

  // Col/Row on one line (hold to repeat)
  scene.add.text(10, y, 'C:', { fontSize: '9px', color: '#888888', fontFamily: 'monospace' }).setDepth(10);
  createRepeatBtn(scene, 24, y - 2, 24, 18, '-', () => host.resizeGrid(host.gridCols - 1, host.gridRows));
  createRepeatBtn(scene, 52, y - 2, 24, 18, '+', () => host.resizeGrid(host.gridCols + 1, host.gridRows));
  scene.add.text(85, y, 'R:', { fontSize: '9px', color: '#888888', fontFamily: 'monospace' }).setDepth(10);
  createRepeatBtn(scene, 99, y - 2, 24, 18, '-', () => host.resizeGrid(host.gridCols, host.gridRows - 1));
  createRepeatBtn(scene, 127, y - 2, 24, 18, '+', () => host.resizeGrid(host.gridCols, host.gridRows + 1));
  y += 24;

  // Undo / Redo buttons
  createSmallBtn(scene, 10, y, 74, 20, '↩ Undo', () => host.undo());
  createSmallBtn(scene, 88, y, 74, 20, '↪ Redo', () => host.redo());
  const undoText = scene.add.text(TOOLBAR_W - 8, y + 22, '↩0 ↪0', {
    fontSize: '8px', color: '#555577', fontFamily: 'monospace',
  }).setOrigin(1, 0).setDepth(10);
  y += 30;

  // Back button
  y += 20;
  const backTarget = host.returnTo;
  createSmallTechButton(scene, TOOLBAR_W / 2, y, '← BACK', TECH.COLOR, () => {
    SoundManager.play('click');
    scene.scene.start(backTarget);
  }, 200);

  // Status bar (below grid area)
  const statusText = scene.add.text(GRID_AREA_X, CANVAS_HEIGHT - 8, 'Paint mode. Click tiles to paint.', {
    fontSize: '10px', color: '#888888', fontFamily: 'monospace',
  }).setOrigin(0, 1).setDepth(10);

  return {
    toolBtns,
    modeBtns,
    decorBtns,
    resourceBtns,
    statusText,
    sizeText,
    undoText,
    pathListText,
    paintSection,
    pathSection,
    decorSection,
    resourceSection,
    eraseSection,
    paletteSectionY,
    paletteVisibleH,
  };
}

// ── Helper functions ──

function addTextToContainer(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  x: number, y: number, text: string, size: string, color: string,
): Phaser.GameObjects.Text {
  const t = scene.add.text(x, y, text, { fontSize: size, color, fontFamily: 'monospace' });
  container.add(t);
  return t;
}

export function createSmallBtn(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number, label: string, onClick: () => void,
): Phaser.GameObjects.Container {
  const container = scene.add.container(x + w / 2, y + h / 2);
  const gfx = scene.add.graphics();
  drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
  container.add(gfx);
  const txt = scene.add.text(0, 0, label, {
    fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
  }).setOrigin(0.5);
  container.add(txt);

  const hitArea = scene.add.rectangle(0, 0, w, h, 0x000000, 0)
    .setInteractive({ useHandCursor: true });
  container.add(hitArea);

  hitArea.on('pointerover', () => {
    gfx.clear();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.HOVER, 1, 1.5);
    txt.setColor('#ffffff');
  });
  hitArea.on('pointerout', () => {
    gfx.clear();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
    txt.setColor('#cccccc');
  });
  hitArea.on('pointerdown', () => { SoundManager.play('click'); onClick(); });
  container.setDepth(10);
  return container;
}

export function createRepeatBtn(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number, label: string, onAction: () => void,
): Phaser.GameObjects.Container {
  const container = scene.add.container(x + w / 2, y + h / 2);
  const gfx = scene.add.graphics();
  drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
  container.add(gfx);
  const txt = scene.add.text(0, 0, label, {
    fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
  }).setOrigin(0.5);
  container.add(txt);

  const hitArea = scene.add.rectangle(0, 0, w, h, 0x000000, 0)
    .setInteractive({ useHandCursor: true });
  container.add(hitArea);

  hitArea.on('pointerover', () => {
    gfx.clear();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.HOVER, 1, 1.5);
    txt.setColor('#ffffff');
  });
  hitArea.on('pointerout', () => {
    gfx.clear();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
    txt.setColor('#cccccc');
    stopRepeat();
  });

  let repeatTimer: Phaser.Time.TimerEvent | null = null;
  let initialTimer: Phaser.Time.TimerEvent | null = null;

  const stopRepeat = () => {
    if (initialTimer) { initialTimer.destroy(); initialTimer = null; }
    if (repeatTimer) { repeatTimer.destroy(); repeatTimer = null; }
  };

  hitArea.on('pointerdown', () => {
    SoundManager.play('click');
    onAction();
    stopRepeat();
    initialTimer = scene.time.delayedCall(350, () => {
      repeatTimer = scene.time.addEvent({
        delay: 100, loop: true, callback: () => { onAction(); },
      });
    });
  });

  hitArea.on('pointerup', stopRepeat);

  container.setDepth(10);
  return container;
}

/**
 * Update mode button visuals to reflect the active mode.
 */
export function updateModeButtons(
  modeBtns: Map<EditorMode, Phaser.GameObjects.Container>,
  activeMode: EditorMode,
): void {
  for (const [m, btn] of modeBtns) {
    const gfx = btn.getAt(0) as Phaser.GameObjects.Graphics;
    const txt = btn.getAt(1) as Phaser.GameObjects.Text;
    const hit = btn.getAt(2) as Phaser.GameObjects.Rectangle;
    const w = hit.width;
    const h = hit.height;
    const active = m === activeMode;
    gfx.clear();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, active ? TECH.HOVER : TECH.COLOR, active ? 1 : 0.6, active ? 1.5 : 1);
    txt.setColor(active ? '#ffffff' : '#cccccc');
  }
}
